/**
 * Domain Layer - RegistrationResult ValueObject
 * 診療予約データ登録結果の値オブジェクト定義
 */

/**
 * 登録詳細情報
 */
export interface RegistrationDetails {
  /** 登録成功件数 */
  readonly successCount: number;
  /** 登録失敗件数 */
  readonly failureCount: number;
  /** 重複件数 */
  readonly duplicateCount: number;
  /** 新規登録件数 */
  readonly newRecords?: number;
  /** 更新件数 */
  readonly updatedRecords?: number;
}

/**
 * 行単位の登録エラー情報
 */
export interface RegistrationRowError {
  /** 行番号 */
  readonly row: number;
  /** AnyBot ID */
  readonly anybotId: string;
  /** エラータイプ */
  readonly errorType: string;
  /** フィールド名 */
  readonly field: string;
  /** エラーメッセージ */
  readonly message: string;
  /** 重要度 */
  readonly severity: 'ERROR' | 'WARNING';
}

/**
 * 登録ステータス
 */
export type RegistrationStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

/**
 * 登録結果を表す値オブジェクト
 */
export interface RegistrationResult {
  /** 成功フラグ */
  readonly success: boolean;
  /** 結果メッセージ */
  readonly message: string;
  /** 登録詳細 */
  readonly details?: RegistrationDetails;
  /** 登録ID */
  readonly registrationId?: string;
  /** 処理時間（ミリ秒） */
  readonly processingTime?: number;
  /** 完了日時（ISO 8601形式） */
  readonly completedAt?: string;
  /** 登録ステータス */
  readonly status?: RegistrationStatus;
  /** エラー一覧 */
  readonly errors?: readonly RegistrationRowError[];
}

/**
 * 成功のRegistrationResultを生成する
 * @param details 登録詳細
 * @param registrationId 登録ID
 * @returns 成功のRegistrationResult
 */
export const createSuccessResult = (
  details: RegistrationDetails,
  registrationId?: string
): RegistrationResult => ({
  success: true,
  message: `${details.successCount}件の登録が完了しました`,
  details,
  registrationId,
  status: 'COMPLETED',
  completedAt: new Date().toISOString(),
});

/**
 * 失敗のRegistrationResultを生成する
 * @param message エラーメッセージ
 * @param errors エラー一覧
 * @returns 失敗のRegistrationResult
 */
export const createFailureResult = (
  message: string,
  errors?: RegistrationRowError[]
): RegistrationResult => ({
  success: false,
  message,
  errors: errors ? Object.freeze([...errors]) : undefined,
  status: 'FAILED',
  completedAt: new Date().toISOString(),
});

/**
 * 処理中のRegistrationResultを生成する
 * @param registrationId 登録ID
 * @returns 処理中のRegistrationResult
 */
export const createPendingResult = (
  registrationId: string
): RegistrationResult => ({
  success: false,
  message: '処理中です',
  registrationId,
  status: 'PROCESSING',
});

/**
 * RegistrationResultが完了しているかどうかを判定する
 * @param result RegistrationResult
 * @returns 完了している場合true
 */
export const isRegistrationComplete = (result: RegistrationResult): boolean => {
  return result.status === 'COMPLETED' || result.status === 'FAILED';
};

/**
 * RegistrationResultに重複エラーがあるかどうかを判定する
 * @param result RegistrationResult
 * @returns 重複エラーがある場合true
 */
export const hasDuplicateErrors = (result: RegistrationResult): boolean => {
  return (result.details?.duplicateCount ?? 0) > 0;
};
